import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/hooks/use-toast';
import {
  beginAiStage,
  approveAiStage,
  rejectAiStage,
  cleanupStaleAiLayers,
  proposalFromWorkflowState,
  type AiStagingSession,
} from '@/lib/aiStagingSession';
import { useAiCreditGating, makeCorrelationId } from '@/hooks/useAiCreditGating';
import { useCreditBalance } from '@/hooks/useCreditBalance';
import { useArtifactMutations } from '@/hooks/useArtifactVersions';
import { aiOps } from '@/lib/aiOpsLog';
import { buildArtifactPersistRequest, type AiProposalMeta } from '@/lib/aiArtifactPersist';
import { regressionCheck, type RegressionResult } from '@/lib/artifactRegressionCheck';
import type { WorkflowState, WorkflowSheet } from '@/types/workflow';

interface UseAiStagingDeps {
  viewOnly: boolean;
  getActiveSheetState: () => WorkflowState;
  loadState: (state: WorkflowState) => void;
  updateActiveSheet: (mut: (sheet: WorkflowSheet) => WorkflowSheet) => void;
  logAction: (verb: string, details?: string) => void;
  openAiAssistant: () => void;
}

export interface AiPreviewState {
  proposalName: string;
  costCredits: number;
  balanceBefore: number;
}

/**
 * Hela AI-staging-flödet bortlyft från Index.tsx (~300 rader).
 *
 * Sköter:
 *   - credit-reservation + preflight
 *   - stage / apply / discard av AI-proposals (begin/approve/rejectAiStage)
 *   - regression-check vid intent='extend' + tillhörande dialog-state
 *   - artifact-version persistering (fire-and-forget) + ai_ops-events
 *   - cleanup av stale proposals vid mount (post-crash)
 *   - `lockedWhilePreview`: wrap:ar handlers så de blockas medan preview
 *     är aktiv (förhindrar exfiltration via Save/Load/Copy/Export).
 */
export function useAiStaging(deps: UseAiStagingDeps) {
  const { viewOnly, getActiveSheetState, loadState, updateActiveSheet, logAction, openAiAssistant } = deps;

  const { preflightAndReserve, commitReservation, releaseReservation } = useAiCreditGating();
  const { balanceFor } = useCreditBalance();
  const { createVersion } = useArtifactMutations();

  const aiSessionRef = useRef<AiStagingSession | null>(null);
  const aiSessionMetaRef = useRef<AiProposalMeta | null>(null);
  const aiSessionProposalRef = useRef<WorkflowState | null>(null);

  const [aiPreview, setAiPreview] = useState<AiPreviewState | null>(null);
  const [aiPreviewPending, setAiPreviewPending] = useState(false);
  const [regressionDialog, setRegressionDialog] = useState<{
    open: boolean;
    result: RegressionResult | null;
  }>({ open: false, result: null });

  const lockedWhilePreview = useCallback(
    <Args extends unknown[]>(fn: (...args: Args) => void, label: string) => {
      return (...args: Args) => {
        if (aiPreview) {
          toast({
            title: 'AI-förslag väntar på beslut',
            description: `${label} är låst tills du trycker Apply eller Discard.`,
            variant: 'destructive',
          });
          return;
        }
        fn(...args);
      };
    },
    [aiPreview],
  );

  const estimateProposalCost = useCallback((state: WorkflowState): number => {
    const b = state.blocks.length;
    const c = state.connectors.length;
    const g = state.groups.length;
    return Math.max(5, Math.ceil(b * 1 + c * 0.5 + g * 2));
  }, []);

  const handleStageAiProposal = useCallback(async (
    proposedState: WorkflowState,
    meta?: AiProposalMeta,
  ) => {
    if (viewOnly) return;
    if (aiSessionRef.current) {
      toast({ title: 'Preview redan aktiv', description: 'Apply eller Discard den först.', variant: 'destructive' });
      return;
    }

    const costCredits = estimateProposalCost(proposedState);
    const balanceBefore = balanceFor('ai');
    const correlationId = makeCorrelationId();

    const pre = await preflightAndReserve({
      kind: 'ai', estimatedAiCredits: costCredits, correlationId, ttlSeconds: 600,
    });
    if (!pre.ok) {
      if (pre.reason === 'insufficient-balance') {
        toast({
          title: 'Otillräckligt med AI-credits',
          description: `Behöver ${costCredits}, har ${pre.balance ?? 0}. Top-up eller kontakta admin.`,
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Reservation failed', description: pre.reason, variant: 'destructive' });
      }
      return;
    }

    const currentState = getActiveSheetState();
    const proposal = proposalFromWorkflowState(
      proposedState,
      proposedState.name || 'AI proposal',
    );
    const { state: staged, session } = beginAiStage(
      currentState, proposal, costCredits, correlationId,
    );
    aiSessionRef.current = session;
    aiSessionMetaRef.current = meta ?? null;
    aiSessionProposalRef.current = proposedState;
    updateActiveSheet(sheet => ({
      ...sheet,
      blocks: staged.blocks,
      connectors: staged.connectors,
      groups: staged.groups,
    }));
    setAiPreview({
      proposalName: session.proposalName,
      costCredits,
      balanceBefore,
    });
    logAction('AI proposal staged', `${proposedState.blocks.length} blocks, cost ${costCredits}`);
  }, [viewOnly, balanceFor, estimateProposalCost, preflightAndReserve, getActiveSheetState, updateActiveSheet, logAction]);

  const performApplyAiProposal = useCallback(async () => {
    const session = aiSessionRef.current;
    if (!session) return;
    const meta = aiSessionMetaRef.current;
    setAiPreviewPending(true);
    try {
      const current = getActiveSheetState();
      const { state: approved, correlationId, actualCost } = approveAiStage(current, session);
      const commitRes = await commitReservation({ correlationId, actualCredits: actualCost });
      if (commitRes.ok) {
        updateActiveSheet(sheet => ({
          ...sheet,
          blocks: approved.blocks,
          connectors: approved.connectors,
          groups: approved.groups,
        }));
        aiSessionRef.current = null;
        aiSessionMetaRef.current = null;
        aiSessionProposalRef.current = null;
        setAiPreview(null);
        logAction('AI proposal applied', `${actualCost} credits consumed`);

        const req = buildArtifactPersistRequest(meta, approved);
        if (req) {
          void (async () => {
            const res = await createVersion(req);
            if (res.versionId && res.artifactId && res.versionNumber !== undefined) {
              void aiOps.artifactCreated({
                conversationId: req.conversationId,
                artifactId: res.artifactId,
                versionId: res.versionId,
                versionNumber: res.versionNumber,
                operation: req.operation,
              });
              toast({
                title: 'Version saved',
                description: `v${res.versionNumber} added to the right-hand panel.`,
              });
            } else {
              // Surface the silent failure path. Previously these
              // errors were swallowed and the sidebar appeared not
              // to update at all.
              toast({
                title: 'Could not save version',
                description: res.error ?? 'create_artifact_version RPC returned no version id. Check that the artifact-versions migrations are applied.',
                variant: 'destructive',
              });
            }
          })();
        }
      }
    } finally {
      setAiPreviewPending(false);
    }
  }, [getActiveSheetState, updateActiveSheet, commitReservation, logAction, createVersion]);

  const handleApplyAiProposal = useCallback(async () => {
    const session = aiSessionRef.current;
    if (!session) return;
    const meta = aiSessionMetaRef.current;
    const proposal = aiSessionProposalRef.current;

    if (meta?.intent === 'extend' && proposal) {
      const result = regressionCheck(session.baseState, proposal, 'extend');
      if (!result.clean) {
        setRegressionDialog({ open: true, result });
        void aiOps.regressionDetected({
          conversationId: meta.conversationId,
          artifactId: '',
          versionId: '',
          missingBlocks: result.missingBlocks.length,
          renamedBlocks: result.renamedBlocks.length,
          removedConnectors: result.removedConnectors,
        });
        return;
      }
    }
    await performApplyAiProposal();
  }, [performApplyAiProposal]);

  const handleRegressionApplyAnyway = useCallback(async () => {
    setRegressionDialog({ open: false, result: null });
    await performApplyAiProposal();
  }, [performApplyAiProposal]);

  const handleRegressionCancel = useCallback(async () => {
    setRegressionDialog({ open: false, result: null });
    const session = aiSessionRef.current;
    if (!session) return;
    setAiPreviewPending(true);
    try {
      const { state: restored, correlationId } = rejectAiStage(session);
      await releaseReservation({ correlationId });
      updateActiveSheet(sheet => ({
        ...sheet,
        blocks: restored.blocks,
        connectors: restored.connectors,
        groups: restored.groups,
      }));
      aiSessionRef.current = null;
      aiSessionMetaRef.current = null;
      aiSessionProposalRef.current = null;
      setAiPreview(null);
      logAction('AI proposal discarded', 'regression-cancel');
    } finally {
      setAiPreviewPending(false);
    }
  }, [updateActiveSheet, releaseReservation, logAction]);

  const handleRegressionRetry = useCallback(async () => {
    const session = aiSessionRef.current;
    const regressionResult = regressionDialog.result;
    const missingNames = regressionResult?.missingBlocks.map((b) => b.name) ?? [];
    const renamedNames = regressionResult?.renamedBlocks.map((b) => b.oldName) ?? [];
    const preserveHint = [...missingNames, ...renamedNames].slice(0, 6).join(', ');

    setRegressionDialog({ open: false, result: null });
    if (!session) return;
    setAiPreviewPending(true);
    try {
      const { state: restored, correlationId } = rejectAiStage(session);
      await releaseReservation({ correlationId });
      updateActiveSheet((sheet) => ({
        ...sheet,
        blocks: restored.blocks,
        connectors: restored.connectors,
        groups: restored.groups,
      }));
      aiSessionRef.current = null;
      aiSessionMetaRef.current = null;
      aiSessionProposalRef.current = null;
      setAiPreview(null);
      logAction('AI proposal reverted', 'regression → return-to-v2');
      openAiAssistant();
      toast({
        title: 'Återgått till v2 — försök igen',
        description: preserveHint
          ? `Skicka ny prompt med t.ex. "bevara: ${preserveHint}".`
          : 'Justera din prompt och skicka igen — blocken är återställda.',
      });
    } finally {
      setAiPreviewPending(false);
    }
  }, [
    regressionDialog.result, updateActiveSheet, releaseReservation, logAction,
    openAiAssistant,
  ]);

  const handleDiscardAiProposal = useCallback(async () => {
    const session = aiSessionRef.current;
    if (!session) return;
    setAiPreviewPending(true);
    try {
      const { state: restored, correlationId } = rejectAiStage(session);
      const releaseRes = await releaseReservation({ correlationId });
      updateActiveSheet(sheet => ({
        ...sheet,
        blocks: restored.blocks,
        connectors: restored.connectors,
        groups: restored.groups,
      }));
      aiSessionRef.current = null;
      aiSessionMetaRef.current = null;
      aiSessionProposalRef.current = null;
      setAiPreview(null);
      logAction('AI proposal discarded', 'reservation released');
      toast({
        title: 'AI-förslag förkastat',
        description: releaseRes.ok
          ? `0 credits förbrukade (${releaseRes.released ?? 0} frigjorda).`
          : 'Förslaget rullades tillbaka.',
      });
    } finally {
      setAiPreviewPending(false);
    }
  }, [updateActiveSheet, releaseReservation, logAction]);

  // Cleanup övergivna AI-proposals vid mount (crash mid-preview).
  useEffect(() => {
    if (viewOnly) return;
    const current = getActiveSheetState();
    const hasStale = current.groups.some(g => (g as { isAiProposal?: boolean }).isAiProposal);
    if (hasStale) {
      const cleaned = cleanupStaleAiLayers(current);
      loadState(cleaned);
      logAction('Stale AI proposal cleaned', 'from previous session');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    aiPreview,
    aiPreviewPending,
    regressionDialog,
    setRegressionDialog,
    lockedWhilePreview,
    handleStageAiProposal,
    handleApplyAiProposal,
    handleDiscardAiProposal,
    handleRegressionApplyAnyway,
    handleRegressionCancel,
    handleRegressionRetry,
  };
}
